import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'dijkstra', label: "Dijkstra's Shortest Path" },
  { key: 'bellman', label: 'Bellman-Ford' },
  { key: 'prim', label: "Prim's MST" },
  { key: 'kruskal', label: "Kruskal's MST" },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Weighted graph view, circular layout ───────────────────────── */
const WeightedGraphView = ({ nodes, edges, directed, active, visited, activeEdges = [] }) => {
  const n = nodes.length;
  const R = 82, cx = 110, cy = 110;
  const pos = nodes.map((_, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return { x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle) };
  });
  const idx = val => nodes.indexOf(val);
  const edgeActive = (a, b) => activeEdges.some(([x, y]) => (x === a && y === b) || (!directed && x === b && y === a));
  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 10 }}>
      <svg width="220" height="220" viewBox="0 0 220 220">
        {directed && (
          <defs>
            <marker id="agpArrow" markerWidth="8" markerHeight="8" refX="15" refY="4" orient="auto">
              <polygon points="0 0, 8 4, 0 8" style={{ fill: 'var(--muted)' }} />
            </marker>
          </defs>
        )}
        {edges.map(([a, b, w], i) => {
          const ai = idx(a), bi = idx(b);
          if (ai === -1 || bi === -1) return null;
          const hl = edgeActive(a, b);
          const mx = (pos[ai].x + pos[bi].x) / 2, my = (pos[ai].y + pos[bi].y) / 2;
          return (
            <g key={i}>
              <line x1={pos[ai].x} y1={pos[ai].y} x2={pos[bi].x} y2={pos[bi].y}
                style={{ stroke: hl ? V1 : 'var(--border-strong)' }} strokeWidth={hl ? 3 : 1.5}
                markerEnd={directed ? 'url(#agpArrow)' : undefined} />
              {w !== undefined && (
                <>
                  <circle cx={mx} cy={my} r="10" style={{ fill: 'var(--card-solid)', stroke: hl ? V1 : 'var(--border)' }} strokeWidth="1" />
                  <text x={mx} y={my + 3.5} textAnchor="middle" fontSize="10" fontWeight="700" style={{ fill: hl ? V1 : 'var(--muted)', fontFamily: 'monospace' }}>{w}</text>
                </>
              )}
            </g>
          );
        })}
        {nodes.map((val, i) => {
          const isActive = active && active.includes(val);
          const isVisited = visited && visited.includes(val);
          return (
            <g key={val}>
              <circle cx={pos[i].x} cy={pos[i].y} r="16" style={{ fill: isActive ? V1 : isVisited ? 'var(--bg2)' : 'var(--bg3)', stroke: isActive ? V1 : isVisited ? GREEN : 'var(--border)' }} strokeWidth="1.5" />
              <text x={pos[i].x} y={pos[i].y + 5} textAnchor="middle" fontSize="13" fontWeight="800" style={{ fill: isActive ? '#fff' : 'var(--text)', fontFamily: "'Inter',sans-serif" }}>{val}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const smallInput = {
  width: 70, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

const DEFAULT_NODES = [0, 1, 2, 3, 4];
const DEFAULT_EDGES = [[0, 1, 4], [0, 2, 1], [2, 1, 2], [1, 3, 5], [2, 3, 8], [3, 4, 3]];
const buildWeightedAdjacency = (nodes, edges, directed) => {
  const adj = {};
  nodes.forEach(n => { adj[n] = []; });
  edges.forEach(([a, b, w]) => {
    if (adj[a]) adj[a].push([b, w]);
    if (!directed && adj[b]) adj[b].push([a, w]);
  });
  return adj;
};

/* ─── Dijkstra's Shortest Path ────────────────────────────────────── */
const DijkstraMode = () => {
  const [startInput, setStartInput] = useState('0');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const start = Number(startInput);
    if (isNaN(start) || !DEFAULT_NODES.includes(start)) return;
    const adj = buildWeightedAdjacency(DEFAULT_NODES, DEFAULT_EDGES, false);
    const dist = {}; DEFAULT_NODES.forEach(n => { dist[n] = Infinity; });
    dist[start] = 0;
    const visited = new Set();
    const built = [];
    while (visited.size < DEFAULT_NODES.length) {
      let node = null, best = Infinity;
      for (const n of DEFAULT_NODES) {
        if (!visited.has(n) && dist[n] < best) { best = dist[n]; node = n; }
      }
      if (node === null) break;
      visited.add(node);
      built.push({ current: node, dist: { ...dist }, visited: [...visited], note: `Pick the unvisited node with the smallest known distance: ${node} (distance ${dist[node] === Infinity ? '∞' : dist[node]}).` });
      for (const [neighbor, w] of adj[node]) {
        if (dist[node] + w < dist[neighbor]) {
          const old = dist[neighbor];
          dist[neighbor] = dist[node] + w;
          built.push({ current: node, neighbor, dist: { ...dist }, visited: [...visited], edge: [node, neighbor], note: `Relax edge ${node}→${neighbor} (weight ${w}): ${dist[node]} + ${w} = ${dist[node] + w}, which beats the old distance (${old === Infinity ? '∞' : old}). Update.` });
        }
      }
    }
    built.push({ done: true, dist: { ...dist } });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'PriorityQueue<int[]> pq = new PriorityQueue<>((a, b) -> a[1] - b[1]);',
      'dist[start] = 0; pq.add(new int[]{start, 0});',
      'while (!pq.isEmpty()) {',
      '    int[] cur = pq.poll();',
      '    if (cur[1] > dist[cur[0]]) continue;   // stale entry',
      '    for (int[] edge : adj.get(cur[0])) {',
      '        int newDist = dist[cur[0]] + edge[1];',
      '        if (newDist < dist[edge[0]]) {',
      '            dist[edge[0]] = newDist;',
      '            pq.add(new int[]{edge[0], newDist});',
      '        }',
      '    }',
      '}',
    ],
    active: !cur || cur.done ? -1 : cur.neighbor !== undefined ? 8 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WeightedGraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} active={cur && !cur.done ? [cur.current] : []} visited={cur ? cur.visited : []} activeEdges={cur && cur.edge ? [cur.edge] : []} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Start node</span>
          <input value={startInput} onChange={e => setStartInput(e.target.value)} style={smallInput} />
          <button onClick={run} style={smallBtn(V1)}>Run Dijkstra</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        {cur && (
          <div style={{ margin: '14px 0' }}>
            {DEFAULT_NODES.map(n => <Chip key={n} color={cur.visited && cur.visited.includes(n) ? GREEN : TEAL}>{n}: {cur.dist[n] === Infinity ? '∞' : cur.dist[n]}</Chip>)}
          </div>
        )}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
          {!cur ? 'This weighted graph has 5 nodes. Pick a start, then Run Dijkstra — it always expands the currently-cheapest unvisited node next.' :
            cur.done ? 'Every node visited — shortest distances finalized.' : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Dijkstra (Min-Heap)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Bellman-Ford ────────────────────────────────────────────────── */
const BF_NODES = [0, 1, 2, 3];
const BF_EDGES = [[0, 1, 4], [0, 2, 5], [1, 2, -3], [2, 3, 4], [1, 3, 6]];
const BellmanFordMode = () => {
  const [negCycle, setNegCycle] = useState(false);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const edges = negCycle ? [...BF_EDGES, [2, 0, -10]] : BF_EDGES;
    const dist = {}; BF_NODES.forEach(n => { dist[n] = Infinity; });
    dist[0] = 0;
    const built = [];
    let cycleFound = false;
    for (let round = 1; round <= BF_NODES.length; round++) {
      let changedThisRound = false;
      for (const [a, b, w] of edges) {
        if (dist[a] !== Infinity && dist[a] + w < dist[b]) {
          if (round === BF_NODES.length) {
            // This is the EXTRA (Vth) round — any improvement here means a negative cycle
            cycleFound = true;
            built.push({ done: true, negativeCycle: true, dist: { ...dist } });
            break;
          }
          const old = dist[b];
          dist[b] = dist[a] + w;
          changedThisRound = true;
          built.push({ round, dist: { ...dist }, edge: [a, b], note: `Round ${round}: relax ${a}→${b} (weight ${w}): ${dist[a] === old ? old : dist[a]} + ${w} = ${dist[b]}, improves from ${old === Infinity ? '∞' : old}.` });
        }
      }
      if (cycleFound) break;
      if (!changedThisRound && round < BF_NODES.length) {
        built.push({ round, dist: { ...dist }, note: `Round ${round}: no distances improved — could stop early, but standard Bellman-Ford always runs V−1 rounds.` });
      }
    }
    if (!cycleFound) built.push({ done: true, negativeCycle: false, dist: { ...dist } });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'for (int i = 0; i < V - 1; i++) {',
      '    for (int[] edge : edges) {',
      '        if (dist[edge[0]] + edge[2] < dist[edge[1]]) {',
      '            dist[edge[1]] = dist[edge[0]] + edge[2];',
      '        }',
      '    }',
      '}',
      '// One more round: if anything STILL improves, a negative cycle exists',
      'for (int[] edge : edges) {',
      '    if (dist[edge[0]] + edge[2] < dist[edge[1]]) return "negative cycle";',
      '}',
    ],
    active: !cur || cur.done ? -1 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WeightedGraphView nodes={BF_NODES} edges={negCycle ? [...BF_EDGES, [2, 0, -10]] : BF_EDGES} directed activeEdges={cur && cur.edge ? [cur.edge] : []} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--muted)', fontWeight: 700, marginBottom: 14 }}>
          <input type="checkbox" checked={negCycle} onChange={e => { setNegCycle(e.target.checked); setSteps(null); }} /> Add an edge 2→0 (weight −10) to create a negative cycle
        </label>
        <button onClick={run} style={smallBtn(V1)}>Run Bellman-Ford (from 0)</button>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        {cur && cur.dist && (
          <div style={{ margin: '14px 0' }}>
            {BF_NODES.map(n => <Chip key={n} color={TEAL}>{n}: {cur.dist[n] === Infinity ? '∞' : cur.dist[n]}</Chip>)}
          </div>
        )}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? 'Unlike Dijkstra, Bellman-Ford handles negative weights correctly (as long as there\'s no negative CYCLE). Run it, or check the box first to see how it detects one.' :
            cur.done ? (cur.negativeCycle ? 'A distance improved even in the extra round — negative cycle detected! No shortest path is well-defined.' : 'V−1 rounds complete, no more improvements — final distances confirmed.') : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Bellman-Ford" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Prim's MST ──────────────────────────────────────────────────── */
const PrimMode = () => {
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const adj = buildWeightedAdjacency(DEFAULT_NODES, DEFAULT_EDGES, false);
    const inMST = new Set([0]);
    const mstEdges = [];
    let totalWeight = 0;
    const built = [{ inMST: [0], mstEdges: [], note: 'Start from node 0 — it\'s in the MST by itself.' }];
    while (inMST.size < DEFAULT_NODES.length) {
      let bestEdge = null, bestWeight = Infinity;
      for (const node of inMST) {
        for (const [neighbor, w] of adj[node]) {
          if (!inMST.has(neighbor) && w < bestWeight) {
            bestWeight = w; bestEdge = [node, neighbor, w];
          }
        }
      }
      if (!bestEdge) break;
      inMST.add(bestEdge[1]);
      mstEdges.push(bestEdge);
      totalWeight += bestEdge[2];
      built.push({ inMST: [...inMST], mstEdges: [...mstEdges], edge: [bestEdge[0], bestEdge[1]], totalWeight, note: `Cheapest edge crossing the frontier: ${bestEdge[0]}→${bestEdge[1]} (weight ${bestEdge[2]}). Add node ${bestEdge[1]} to the MST.` });
    }
    built.push({ done: true, mstEdges: [...mstEdges], totalWeight });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'PriorityQueue<int[]> pq = new PriorityQueue<>((a, b) -> a[2] - b[2]);',
      'Set<Integer> inMST = new HashSet<>();',
      'inMST.add(start);',
      'for (int[] edge : adj.get(start)) pq.add(edge);',
      'while (inMST.size() < n && !pq.isEmpty()) {',
      '    int[] edge = pq.poll();   // cheapest edge crossing the frontier',
      '    if (inMST.contains(edge[1])) continue;   // both ends already in',
      '    inMST.add(edge[1]);',
      '    for (int[] next : adj.get(edge[1])) pq.add(next);',
      '}',
    ],
    active: !cur || cur.done ? -1 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WeightedGraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} visited={cur ? cur.inMST : []} activeEdges={cur ? cur.mstEdges.map(([a, b]) => [a, b]) : []} />
        <button onClick={run} style={smallBtn(V1)}>Run Prim's (from node 0)</button>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        {cur && cur.totalWeight !== undefined && <p style={{ margin: '14px 0 0' }}><Chip color={GREEN}>MST total weight: {cur.totalWeight}</Chip></p>}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? 'Prim\'s grows the MST outward from a single starting node, always adding the cheapest edge that crosses the current frontier.' :
            cur.done ? `MST complete — ${cur.mstEdges.length} edges, total weight ${cur.totalWeight}.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Prim's MST" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Kruskal's MST ───────────────────────────────────────────────── */
const KruskalMode = () => {
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const sorted = [...DEFAULT_EDGES].sort((a, b) => a[2] - b[2]);
    const parent = {}; DEFAULT_NODES.forEach(n => { parent[n] = n; });
    const find = x => (parent[x] === x ? x : (parent[x] = find(parent[x])));
    const mstEdges = [];
    let totalWeight = 0;
    const built = [];
    for (const [a, b, w] of sorted) {
      const rootA = find(a), rootB = find(b);
      if (rootA === rootB) {
        built.push({ edge: [a, b], mstEdges: [...mstEdges], skipped: true, note: `Edge ${a}-${b} (weight ${w}): both already in the same component — would create a cycle. Skip.` });
      } else {
        parent[rootA] = rootB;
        mstEdges.push([a, b, w]);
        totalWeight += w;
        built.push({ edge: [a, b], mstEdges: [...mstEdges], totalWeight, note: `Edge ${a}-${b} (weight ${w}): different components — add it to the MST.` });
      }
    }
    built.push({ done: true, mstEdges: [...mstEdges], totalWeight });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'Arrays.sort(edges, (a, b) -> a[2] - b[2]);   // sort by weight ascending',
      'for (int[] edge : edges) {',
      '    int rootA = find(edge[0]), rootB = find(edge[1]);',
      '    if (rootA == rootB) continue;   // would create a cycle — skip',
      '    union(rootA, rootB);',
      '    mstEdges.add(edge);',
      '}',
    ],
    active: !cur || cur.done ? -1 : cur.skipped ? 3 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WeightedGraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} activeEdges={cur ? (cur.mstEdges || []).map(([a, b]) => [a, b]) : []} />
        <button onClick={run} style={smallBtn(V1)}>Run Kruskal's</button>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        {cur && cur.totalWeight !== undefined && <p style={{ margin: '14px 0 0' }}><Chip color={GREEN}>MST total weight: {cur.totalWeight}</Chip></p>}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? 'Kruskal\'s sorts every edge by weight, then greedily adds each one if (and only if) it doesn\'t connect two nodes already in the same component (checked via Union-Find).' :
            cur.done ? `MST complete — ${cur.mstEdges.length} edges, total weight ${cur.totalWeight}.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Kruskal's MST" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const edge = DEFAULT_EDGES[Math.floor(Math.random() * DEFAULT_EDGES.length)];
  const [from, to, w] = edge;
  const currentDistFrom = Math.floor(Math.random() * 6);
  const currentDistTo = Math.floor(Math.random() * 12);
  const newDist = currentDistFrom + w;
  const correct = newDist < currentDistTo ? 'yes' : 'no';
  return { from, to, w, currentDistFrom, currentDistTo, newDist, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  return (
    <div>
      <WeightedGraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} active={[scenario.from, scenario.to]} activeEdges={[[scenario.from, scenario.to]]} />
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        Current known distance to {scenario.from}: {scenario.currentDistFrom}. Current known distance to {scenario.to}: {scenario.currentDistTo}. Edge {scenario.from}→{scenario.to} has weight {scenario.w}. Does relaxing this edge improve dist[{scenario.to}]?
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['yes', 'no'].map(o => {
          const revealed = answer !== null;
          const isCorrect = o === scenario.correct;
          const isPicked = o === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o} onClick={() => pick(o)} disabled={revealed} style={{
              padding: '10px 20px', borderRadius: 10, border: `1.5px solid ${border}`, background: bg, color,
              fontSize: 13.5, fontWeight: 700, cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif", textTransform: 'capitalize',
            }}>{o}</button>
          );
        })}
      </div>
      {answer !== null && (
        <>
          <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 10 }}>
            {scenario.currentDistFrom} + {scenario.w} = {scenario.newDist}, versus current {scenario.currentDistTo} — {scenario.correct === 'yes' ? 'that\'s an improvement.' : 'not an improvement.'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={next} style={smallBtn(V1)}>Next Question</button>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const AdvancedGraphPlayground = () => {
  const [mode, setMode] = useState('dijkstra');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'dijkstra' && <DijkstraMode />}
      {mode === 'bellman' && <BellmanFordMode />}
      {mode === 'prim' && <PrimMode />}
      {mode === 'kruskal' && <KruskalMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default AdvancedGraphPlayground;
